import hensel from "@/images/trusted/hensel.webp";
import kreditplus from "@/images/trusted/kreditplus.webp";
import inlingua from "@/images/trusted/inlingua.webp";
import dhi from "@/images/trusted/dhi.webp";
import accusentry from "@/images/trusted/accusentry.webp";
import kanaFurniture from "@/images/trusted/kana-furniture.webp";
import wss from "@/images/trusted/wss.webp";
import hayana from "@/images/trusted/hayana.webp";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useAutoplay } from "@/lib/use-autoplay";
import { cn } from "@/lib/utils";

/** How long each logo position holds before the row moves on. */
const LOGO_DELAY_MS = 3500;

const logos = [
  { name: "Hensel", src: hensel },
  { name: "KreditPlus", src: kreditplus },
  { name: "inlingua Indonesia", src: inlingua },
  { name: "DHI", src: dhi },
  { name: "AccuSentry", src: accusentry },
  { name: "KANA Furniture", src: kanaFurniture },
  { name: "PT Wirayudha Sarana Sakti", src: wss },
  { name: "Hayana Solindo Utama", src: hayana },
];

/**
 * Company logos that have Kanata desks in their offices.
 *
 * The logo files sit on an off-white ground, not transparency, so they are
 * multiplied onto the white cards and the edge of each file never shows.
 * As a grid the list wraps centred, so an odd count leaves the last row
 * balanced. As a carousel (the home page's Kantor section, DEL-522) it is
 * one row, edge to edge of the screen, that moves on its own, with arrows.
 */
export function TrustedBy({
  tone = "light",
  heading: Heading = "h2",
  layout = "grid",
  className,
}: {
  tone?: "light" | "dark";
  layout?: "grid" | "carousel";
  /** h3 when it sits inside a section that already has its own h2. */
  heading?: "h2" | "h3";
  className?: string;
}) {
  return (
    <div className={className}>
      <Heading
        className={cn(
          "px-5 text-center text-2xl font-medium tracking-tight sm:text-3xl",
          tone === "dark" ? "text-white" : "text-ink",
        )}
      >
        Dipercaya perusahaan{" "}
        <span className={tone === "dark" ? "text-brand-bright" : "text-brand"}>se-Indonesia</span>
      </Heading>
      {layout === "carousel" ? (
        <LogoCarousel />
      ) : (
        <ul className="mx-auto mt-8 flex max-w-5xl flex-wrap justify-center gap-3 sm:gap-5">
          {logos.map((logo) => (
            <li
              key={logo.name}
              className={cn(CARD, "w-[calc(50%-0.375rem)] sm:w-[calc(50%-0.625rem)] md:w-[calc(25%-0.9375rem)]")}
            >
              <LogoImage logo={logo} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CARD =
  "flex h-24 items-center justify-center rounded-2xl bg-white px-6 shadow-[0_6px_24px_-8px_rgb(22_33_28/0.18)] sm:h-28";

function LogoImage({ logo, decorative = false }: { logo: (typeof logos)[number]; decorative?: boolean }) {
  return (
    <img
      src={logo.src}
      alt={decorative ? "" : logo.name}
      loading="lazy"
      className="max-h-10 w-auto max-w-full mix-blend-multiply sm:max-h-12"
    />
  );
}

/**
 * The row runs the full width of the screen, so there are more cards than
 * logos: the list repeats until the row is full and can loop. Divine knows
 * the repeats show; he wants the row full ("kasih kesan banyak"). Repeats
 * are hidden from screen readers.
 */
const ROW = Array.from({ length: Math.max(2, Math.ceil(16 / logos.length)) }, (_, copy) =>
  logos.map((logo) => ({ logo, copy })),
).flat();

function LogoCarousel() {
  const { plugins, setApi } = useAutoplay(LOGO_DELAY_MS);
  return (
    <Carousel
      setApi={setApi}
      opts={{ align: "start", loop: true }}
      plugins={plugins}
      className="mt-8"
      aria-label="Perusahaan yang memakai Kanata"
    >
      <CarouselContent className="-ml-3 py-3 sm:-ml-5">
        {ROW.map(({ logo, copy }) => (
          <CarouselItem
            key={`${copy}-${logo.name}`}
            aria-hidden={copy > 0 || undefined}
            className="basis-[42%] pl-3 sm:basis-1/4 sm:pl-5 lg:basis-1/6 2xl:basis-[12.5%]"
          >
            <div className={CARD}>
              <LogoImage logo={logo} decorative={copy > 0} />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious variant="white" className="left-3 size-9 text-ink shadow-md sm:left-5 sm:size-10" />
      <CarouselNext variant="white" className="right-3 size-9 text-ink shadow-md sm:right-5 sm:size-10" />
    </Carousel>
  );
}
